/**
 * FEL PÅ KLIENTSIDAN.
 *
 * TEXTEN KOMMER ALLTID UR shared/errors.ts, ALDRIG UR SVARET.
 * Servern skickar med en message_sv, och den är i dag ordagrant samma sträng
 * (worker/src/http/errors.ts slår upp den i ERRORS). Klienten använder ändå sin
 * EGEN kopia av två skäl: en gammal eller manipulerad server ska inte kunna
 * skriva text rakt in i gränssnittet, och texten ska vara densamma även när
 * felet uppstod utan att nätet var inblandat. Serverns sträng behålls i
 * `serverns_text` för loggning och för att kunna upptäcka att de glidit isär.
 *
 * FYRA SVAR SOM INTE ÄR ApiErrorBody. Workern svarar med flit UTAN felkod i
 * fyra lägen (worker/src/http/respond.ts och router/router.ts):
 *   501 { not_built: true, route, message_sv }   rutten står i kontraktet men
 *                                                är inte byggd. TVÅ RUTTER
 *                                                SVARAR SÅ I DAG: FILE_UPLOAD
 *                                                och FILE_DOWNLOAD
 *                                                (worker/src/modules/steg3/).
 *                                                VOICE_TRANSCRIBE är byggd.
 *   404 { not_found: true, message_sv }          adressen finns inte
 *   405 { method_not_allowed: true, allow, ... } fel metod
 *   -   inget svar alls                          nätet
 * Ingen ErrorCode betyder något av det, och att låna en (SERVER_ERROR eller
 * SERVICE_UNAVAILABLE, båda retry 'retry') hade satt en telefon att skicka om i
 * evighet för något som inte kommer att svara. De får därför egna slag nedan.
 *
 * RESULTAT I STÄLLET FÖR KAST. Varje anrop lämnar ett Resultat, och tsc tvingar
 * anroparen att pröva `ok` innan han rör värdet. En klient där felhanteringen
 * är frivillig blir en klient där den saknas på halva ställena — och de svenska
 * texterna i shared/errors.ts är skrivna för att VISAS, inte för att sväljas i
 * en console.error.
 */

import { ERRORS } from '@delat/errors';
import type { ErrorCode, ErrorDetailFor, ErrorSpec, RetryPolicy } from '@delat/errors';

/**
 * Ett fel som bär en riktig kod ur kontraktet, MED DEN DETALJ JUST DEN KODEN
 * FÅR BÄRA.
 *
 * FORMEN ÄR EN DISKRIMINERAD UNION PÅ `code`, precis som ApiErrorBody i
 * shared/errors.ts — och av exakt samma skäl. Kontraktet stänger två av
 * detaljerna med flit:
 *   ROW_INVOICED   { table, id } och INGENTING MER. invoice_run_id är
 *                  fakturadata och får aldrig nå montörs- eller gästläge.
 *   PRICE_MISSING  { role } och INGENTING MER. price_list_id är prisdata,
 *                  samma sak.
 * En öppen `detalj: Record<string, ...>` hade låtit precis de fälten slinka
 * igenom utan att tsc sa ett ord. Med unionen nedan får en skärm som narrows
 * på `fel.code === 'ROW_INVOICED'` en detalj med två fält, och den kan inte
 * läsa ett tredje ens av misstag. Det är beslut 16 och 17 genomdrivna i
 * KLIENTEN och inte bara på servern.
 *
 * Att narrow på `code` är alltså inte en artighet utan vägen in i detaljen:
 *
 *     if (fel.slag === 'api' && fel.code === 'WEEK_LOCKED') {
 *       visaVeckan(fel.detalj?.['iso_week']);   // öppen form, går bra
 *     }
 */
export interface ApifelFor<TCode extends ErrorCode> {
  readonly slag: 'api';
  readonly code: TCode;
  /** ERRORS[code].message_sv. Visas ORDAGRANT. */
  readonly text: string;
  readonly spec: ErrorSpec;
  readonly http_status: number;
  /** Formen per kod står i shared/errors.ts. undefined när koden saknar detail. */
  readonly detalj: ErrorDetailFor<TCode> | undefined;
  /** Serverns egen sträng. Bara för loggning — visa den aldrig. */
  readonly serverns_text: string;
}

export type Apifel = { [TCode in ErrorCode]: ApifelFor<TCode> }[ErrorCode];

/** 501: rutten finns i kontraktet men är inte byggd än. */
export interface EjByggtfel {
  readonly slag: 'ej_byggd';
  readonly rutt: string;
  readonly text: string;
}

/** 404 från routern: sökvägen finns inte. En klientbugg, inte ett användarfel. */
export interface Saknasfel {
  readonly slag: 'saknas';
  readonly text: string;
}

/** 405: sökvägen finns, metoden inte. Också en klientbugg. */
export interface FelMetodfel {
  readonly slag: 'fel_metod';
  readonly tillatna: readonly string[];
  readonly text: string;
}

/** fetch kastade: flygplansläge, tappat nät, avbrutet anrop. */
export interface Natfel {
  readonly slag: 'nat';
  readonly text: string;
  readonly avbrutet: boolean;
}

/**
 * Svaret gick inte att tolka: trasig JSON, en status utan kropp, eller ett
 * granskningsfel (svaret hade fel form för sin typ). Sista utvägen, aldrig
 * något användaren kan göra åt — men det ska ändå sägas något begripligt.
 */
export interface Obegripligtfel {
  readonly slag: 'obegripligt';
  readonly http_status: number;
  readonly text: string;
  readonly orsak: string;
}

export type Klartidfel =
  | Apifel
  | EjByggtfel
  | Saknasfel
  | FelMetodfel
  | Natfel
  | Obegripligtfel;

export type Resultat<T> =
  | { readonly ok: true; readonly varde: T }
  | { readonly ok: false; readonly fel: Klartidfel };

export const lyckades = <T>(varde: T): Resultat<T> => ({ ok: true, varde });
export const misslyckades = <T>(fel: Klartidfel): Resultat<T> => ({ ok: false, fel });

/* --- Konstruktorer ------------------------------------------------------ */

/**
 * ENDA VÄGEN TILL ETT Apifel. Argumentet `detalj` är typat PER KOD, så ett
 * försök att skicka med ett price_list_id till PRICE_MISSING faller redan här.
 *
 * DEN ENDA CASTEN I MODULEN, och shared/errors.ts har redan skrivit ut varför
 * den behövs: "en union går inte att sätta ihop ur en GENERISK code:
 * ErrorCode. Felkroppar byggs därför genom en hjälpare som är typad per kod
 * ... Det är priset för att beslut 16:s pekare inte kan slinka med i detail,
 * och det är medvetet betalt." Samma pris, samma skäl, ett enda ställe.
 */
export const apifel = <TCode extends ErrorCode>(
  code: TCode,
  detalj: ErrorDetailFor<TCode> | undefined,
  servernsText: string,
): Apifel =>
  ({
    slag: 'api',
    code,
    text: ERRORS[code].message_sv,
    spec: ERRORS[code],
    http_status: ERRORS[code].http_status,
    detalj,
    serverns_text: servernsText,
  }) as Apifel;

export const ejByggtfel = (rutt: string, text: string): EjByggtfel => ({
  slag: 'ej_byggd',
  rutt,
  // Serverns text är HÄR den enda som finns: 501 är inte en ErrorCode och har
  // ingen svensk text i kontraktet. Modulen som äger rutten skriver den
  // (registerNotBuilt i workern), så den är vår egen och säger vad som fattas.
  text: text === '' ? 'Den här delen är inte byggd än.' : text,
});

export const natfel = (avbrutet: boolean): Natfel => ({
  slag: 'nat',
  avbrutet,
  text: avbrutet
    ? 'Anropet avbröts.'
    : 'Ingen kontakt med Klartid just nu. Det du sparat ligger kvar i telefonen.',
});

export const obegripligtfel = (httpStatus: number, orsak: string): Obegripligtfel => ({
  slag: 'obegripligt',
  http_status: httpStatus,
  orsak,
  text: 'Något gick fel hos oss. Försök igen om en stund.',
});

/* --- Frågor som gränssnittet ställer ------------------------------------ */

/** Texten som visas för användaren. Alla slag har en. */
export const feltext = (fel: Klartidfel): string => fel.text;

/**
 * Vad utkorgen ska göra med EN operation (shared/errors.ts, retry-fältet).
 * 'retry' ligger kvar och skickas om automatiskt, 'hold' ligger kvar tills
 * hindret är undanröjt, 'give_up' flyttas till "Behöver åtgärd".
 *
 * BARA MENINGSFULLT FÖR ETT OPERATIONSFEL. Faller HELA anropet ligger varje
 * operation kvar orörd oavsett vad koden säger — se HELANROPSFEL överst i
 * shared/errors.ts, och särskilt FORBIDDEN, som är märkt 'give_up' men också
 * kan vara ett helanropsfel. Steg 5 äger den distinktionen; funktionen finns
 * här för att den inte ska uppfinnas fyra gånger.
 */
export const atgardFor = (fel: Klartidfel): RetryPolicy =>
  fel.slag === 'api' ? fel.spec.retry : fel.slag === 'nat' ? 'retry' : 'give_up';

/**
 * DE TVÅ KODER SOM BETYDER "LOGGA IN IGEN". Båda är 'hold': ingenting är
 * förlorat, utkorgen ligger kvar och skickas oförändrad efter inloggningen.
 * LICENSE_EXPIRED får ALDRIG bli en kryptisk krasch — det är den normala
 * utgången för en telefon som varit utan nät i mer än LICENSE_TTL_DAYS dagar.
 */
export const kraverOmloggning = (fel: Klartidfel): boolean =>
  fel.slag === 'api' && (fel.code === 'UNAUTHENTICATED' || fel.code === 'LICENSE_EXPIRED');

/** Prenumerationen är pausad: allt går att läsa och exportera, inget att spara. */
export const arLaslage = (fel: Klartidfel): boolean =>
  fel.slag === 'api' && fel.code === 'SUBSCRIPTION_LOCKED';

/** Är det HÄR felet ett fel i vår egen kod snarare än något användaren gjort? */
export const arKlientbugg = (fel: Klartidfel): boolean =>
  fel.slag === 'saknas' || fel.slag === 'fel_metod' || fel.slag === 'obegripligt';

/**
 * Känner igen en ApiErrorBody i en tolkad JSON-kropp. Koden MÅSTE finnas i
 * ERRORS; en okänd sträng är inte en felkod utan skräp, och då är svaret
 * obegripligt och inte ett fel vi kan åtgärda.
 */
export const arKandFelkod = (varde: unknown): varde is ErrorCode =>
  typeof varde === 'string' && Object.prototype.hasOwnProperty.call(ERRORS, varde);
